import * as React from 'react';
import { Card, List, Typography, ListItemText, Box } from '@mui/material';
import { InfoListItem, ChannelValue } from '@brightlayer-ui/react-components/core';
import { Data, isDataLoaded, DataState, isDataError } from '../../types/data-providers';
import { DataErrorIndicator } from '../DataErrorIndicator';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { getPrecisionValue } from '../../lib/precision-helper';
import { AppRoute } from '../../routes';
import { getUpdateChannelData } from '../../lib/signalr-helper';
import { DevicePropertiesDetails, DeviceChannelData } from '../../types/device';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';
import _ from 'lodash';
import { Status } from '../../types';
import { DataEmptyIndicator } from '../DataEmptyIndicator';
import { useGetSiteSubscriptionDetails } from '../../data/DataProviders/GetSiteSubscriptionProvider';
import { StatusTag } from '../StatusTag/StatusTag';
type DeviceData = {
    deviceProperties: Data<DevicePropertiesDetails>;
    channelData: Data<DeviceChannelData[]>;
};

const DeviceDetailRow: React.FC<{
    title: string;
    rightComponent?: JSX.Element;
    removeDivider?: boolean;
}> = (props) => (
    <div data-testid="device-detail-row">
        <InfoListItem
            title=""
            dense
            hidePadding
            rightComponent={
                <Box width={750}>
                    <div>
                        <Typography
                            style={{
                                textOverflow: 'ellipsis',
                                wordWrap: 'break-word',
                                textAlign: 'end',
                                overflow: 'hidden',
                            }}
                        >
                            {props.rightComponent}
                        </Typography>
                    </div>
                </Box>
            }
            leftComponent={
                <ListItemText>
                    <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        <Typography variant="body2" display="inline">
                            {props.title}
                        </Typography>
                    </div>
                </ListItemText>
            }
            divider={props.removeDivider ? undefined : 'full'}
        />
    </div>
);

export const DeviceInformation: React.FC<{ deviceData: DeviceData; appRoute: AppRoute }> = (props) => {
    const { t } = useLanguageLocale();

    const realTimeSiteData = useSignalRSiteData();
    const [channelData, setChannelData] = React.useState<Data<DeviceChannelData[]>>({ state: DataState.Loading });
    const meshSiteSubscriptionDetails = useGetSiteSubscriptionDetails();
    const [subscriptionType, setSubscriptionType] = React.useState<string>();
    const [filterChannelArray, setFilterChannelArray] = React.useState<DeviceChannelData[]>([]);
    React.useEffect(() => {
        if (meshSiteSubscriptionDetails !== null) {
            if (isDataLoaded(meshSiteSubscriptionDetails)) {
                setSubscriptionType(meshSiteSubscriptionDetails.value.subscriptionDetails.partNumber);
            }
            if (isDataError(meshSiteSubscriptionDetails)) {
                setSubscriptionType('');
            }
        }
    }, [meshSiteSubscriptionDetails]);
    React.useEffect(() => {
        setChannelData(props.deviceData.channelData);
    }, [props.deviceData.channelData]);
    // React.useEffect(() => {

    React.useEffect(() => {
        if (subscriptionType === 'BLI-CH-DL-ESS' && isDataLoaded(channelData) && channelData.value.length > 0) {
            const essentialChannelId = ['10251019', '10251776', '10251777', '10214652', '10214653'];
            const filteredChannel = channelData.value.map((channel: any) => ({
                ...channel,
                isEssential: essentialChannelId.includes(channel.id),
            }));

            setFilterChannelArray(filteredChannel);
        }
    }, [channelData, meshSiteSubscriptionDetails, subscriptionType]);
    React.useEffect(() => {
        if (realTimeSiteData && realTimeSiteData.channelData) {
            if (isDataLoaded(props.deviceData.deviceProperties)) {
                const channelsList = _.filter(realTimeSiteData.channelData, {
                    deviceId: props.deviceData.deviceProperties.value.id,
                });

                const clone = getUpdateChannelData(channelsList, props.deviceData.channelData);
                setChannelData(clone);
            }
        }
    }, [realTimeSiteData]); // eslint-disable-line react-hooks/exhaustive-deps
    const emptyValue = (value: string | undefined | null, channelId: string | number | undefined | null): string => {
        // "id": "10265719", for Last Command Received"
        if (channelId?.toString() === '10265719' && value) {
            return `0X${value}`;
        }
        if (channelId?.toString() === '10265718' && value) {
            return `0X${value}`;
        }
        return getPrecisionValue(value) || t('DEVICE_DETAILS.EMPTY_VALUE') || '';

    }
    const emptyValue1 = (value: string | undefined | null, channelId: string | number | undefined | null, isEssential?: boolean): any => {
        if (isDataLoaded(channelData) || filterChannelArray.length > 0) {
            // "id": "10265719", for Last Command Received"
            if (channelId?.toString() === '10265719' && value) {
                return `0X${value}`;
            }
            if (isEssential === false) {
                return getPrecisionValue(value) || t('DEVICE_DETAILS.EMPTY_VALUE') || '';
            }

            return (
                <StatusTag
                    status={Status.UnSupported}
                // style={{ textAlign: 'center', borderWidth: '2px', borderColor: 'black' }}
                />
            );
        }
    };

    return (
        <div>
            <Card>
                <List disablePadding>
                    <DataErrorIndicator
                        inline
                        message={t('DEVICE_DETAILS.DEVICE_CONNECTION_ERROR')}
                        data={props.deviceData.deviceProperties}
                    />

                    <DataErrorIndicator
                        inline
                        message={t('DEVICE_DETAILS.CHANNEL_DATA_ERROR')}
                        data={props.deviceData.channelData}
                    />
                    {isDataLoaded(channelData) && (
                        <DataEmptyIndicator
                            icon="Device"
                            message={t('DEVICE_DETAILS.CHANNEL_DATA_ERROR')}
                            data={channelData}
                            isEmpty={channelData.value.length === 0}
                        />
                    )}
                    {subscriptionType === 'BLI-CH-DL-ESS'
                        ? filterChannelArray.length > 0 &&
                        filterChannelArray.map((channel, index) => (
                            <DeviceDetailRow
                                key={index}
                                title={channel.name || ''}
                                rightComponent={
                                    <ChannelValue
                                        value={emptyValue1(channel.value, channel.id, channel.isEssential)}
                                        units={channel?.isEssential ? '' : channel.unit ?? ''}
                                    />
                                }
                            />
                        ))
                        : isDataLoaded(channelData) &&
                        channelData.value.length > 0 &&
                        channelData.value.map((channel, index) => (
                            <DeviceDetailRow
                                key={index}
                                title={channel.name || ''}
                                rightComponent={
                                    <ChannelValue value={emptyValue(channel.value, channel.id)} units={channel.unit || ''} />
                                }
                            />
                        ))}
                </List>
            </Card>
        </div>
    );
};
